import {
  CoursesContextBrand,
  NonEmptyFeature,
  ProgramDecision,
  ProgramFeature,
  Term,
} from "common/api/decimal/methods/course.ts";

function isNonEmptyFeature(
  feature: ProgramFeature
): feature is NonEmptyFeature {
  return !!feature.text?.trim();
}

interface ProgramConfig {
  equitable_access: {
    programName: string;
    programDescription: string;
    itemCardDescription: string;
    features: NonEmptyFeature[];
    akademos: {
      updateShippingLink: string;
      personalizedValueSheet: string;
    };
    catalogLevelOptOut: boolean;
    optOutMessage: string | null;
  };
  inclusive_access: {
    programName: string;
    programDescription: string;
    features: NonEmptyFeature[];
    showPricing: boolean;
  };
  no_program: {
    programName: string;
    programDescription: string;
    features: NonEmptyFeature[];
    showPVSActualMarketPrice: boolean;
  };
  personalizedValueSheet: {
    showPriceDrawer: boolean;
    showPricing: boolean;
    showActualMarketPrice: boolean;
    showCreditHourBreakdownInPvsDrawer: boolean;
  };
}
interface SharedConfig {
  name: string;
  description: string;
}

export type ProgramScope = "ia_only" | "ea_only" | "flexible" | null;

interface PersonalizedValueSheetConfig {
  showPriceDrawer: boolean;
  showPricing: boolean;
  showActualMarketPrice: boolean;
  showCreditHourBreakdownInPvsDrawer: boolean;
}

export interface ProgramDetailsType extends SharedConfig {
  scope: ProgramScope;
  userEffectiveProgram: ProgramDecision | null;
  participating: boolean;
  allowsPrint: boolean;
  np: NoProgram;
  ea: EaOnlyProgram;
  ia: IaOnlyProgram;
  personalizedValueSheet: PersonalizedValueSheetConfig;
}
interface EaOnlyProgram extends SharedConfig {
  features: NonEmptyFeature[];
  optOutMessage: string;
  itemCardDescription: string;
  updateShippingLink: string;
  personalizedValueSheet: string;
  allowsOptOut: boolean;
}

type NoProgram = SharedConfig & {
  allowsPrint: boolean;
  features: NonEmptyFeature[];
  showPVSActualMarketPrice: boolean;
};

type IaOnlyProgram = SharedConfig & {
  features: NonEmptyFeature[];
  showPricing: boolean;
  allowsPrint: boolean;
};

const emptyBrandConfig: ProgramConfig = {
  equitable_access: {
    programName: "",
    programDescription: "",
    itemCardDescription: "",
    features: [],
    akademos: {
      updateShippingLink: "",
      personalizedValueSheet: "",
    },
    catalogLevelOptOut: false,
    optOutMessage: null,
  },
  inclusive_access: {
    programName: "",
    programDescription: "",
    features: [],
    showPricing: true,
  },
  no_program: {
    programName: "",
    programDescription: "",
    features: [],
    showPVSActualMarketPrice: false,
  },
  personalizedValueSheet: {
    showPriceDrawer: false,
    showPricing: false,
    showActualMarketPrice: false,
    showCreditHourBreakdownInPvsDrawer: false,
  },
};

const buildProgramConfig = (
  brand: CoursesContextBrand | null
): ProgramConfig => {
  if (!brand) {
    return emptyBrandConfig;
  }
  return {
    equitable_access: {
      programName: brand.ea?.programName,
      programDescription: brand.ea?.programDescription,
      itemCardDescription: brand.ea?.itemCardDescription,
      features: brand.ea?.programFeatures?.filter(isNonEmptyFeature),
      akademos: {
        updateShippingLink: brand.ea?.akademos?.updateShippingLink,
        personalizedValueSheet: brand.ea?.akademos?.personalizedValueSheet,
      },
      catalogLevelOptOut: brand.ea?.catalogLevelOptOut,
      optOutMessage: brand.ea?.optOutMessage,
    },
    inclusive_access: {
      programName: brand.iaProgramName,
      programDescription: brand.iaProgramDescription,
      features: brand.iaProgramFeatures?.filter(isNonEmptyFeature),
      showPricing: brand.showIaPricing ?? true,
    },
    no_program: {
      programName: brand.optOutProgramName,
      programDescription: brand.optOutProgramDescription,
      features: brand.optOutProgramFeatures?.filter(isNonEmptyFeature),
      showPVSActualMarketPrice:
        brand.personalizedValueSheet?.showActualMarketPrice,
    },
    personalizedValueSheet: {
      showPriceDrawer: brand.personalizedValueSheet?.showPriceDrawer ?? false,
      showPricing: brand.personalizedValueSheet?.showPricing ?? false,
      showActualMarketPrice:
        brand.personalizedValueSheet?.showActualMarketPrice ?? false,
      showCreditHourBreakdownInPvsDrawer:
        brand.personalizedValueSheet?.showCreditHourBreakdownInPvsDrawer ??
        false,
    },
  };
};

export const emptyProgramDetails: ProgramDetailsType = {
  name: "",
  description: "",
  scope: null,
  participating: false,
  userEffectiveProgram: null,
  allowsPrint: true,
  np: {
    name: "",
    description: "",
    allowsPrint: true,
    features: [],
    showPVSActualMarketPrice: false,
  },
  ia: {
    name: "",
    description: "",
    features: [],
    showPricing: true,
    allowsPrint: true,
  },
  ea: {
    name: "",
    description: "",
    features: [],
    optOutMessage: "",
    itemCardDescription: "",
    updateShippingLink: "",
    personalizedValueSheet: "",
    allowsOptOut: false,
  },
  personalizedValueSheet: {
    showPriceDrawer: false,
    showPricing: false,
    showActualMarketPrice: false,
    showCreditHourBreakdownInPvsDrawer: false,
  },
};

const getProgramName = (forDecision: ProgramDecision, config: ProgramConfig) =>
  config[forDecision]?.programName ?? "";

const getProgramDescription = (
  forDecision: ProgramDecision,
  config: ProgramConfig
) => config[forDecision]?.programDescription ?? "";

const getProgramFeatures = (
  forDecision: ProgramDecision,
  config: ProgramConfig
) => config[forDecision]?.features ?? null;

export default function buildProgramDetails({
  brand,
  term,
}: {
  brand: CoursesContextBrand;
  term: Term;
}): ProgramDetailsType {
  if (!term || !brand) {
    return emptyProgramDetails;
  }

  const { inclusiveAccessExtras, noProgramExtras } = term;

  const { institutionProgram, programDecision } = term;
  if (!programDecision) {
    return emptyProgramDetails;
  }

  const config = buildProgramConfig(brand);
  const participating =
    programDecision && programDecision !== "no_program" ? true : false;
  const scope =
    institutionProgram === "inclusive_access"
      ? "ia_only"
      : institutionProgram === "equitable_access"
      ? "ea_only"
      : institutionProgram === "flexible_access"
      ? "flexible"
      : null;

  const allowsPrint =
    programDecision === "no_program"
      ? noProgramExtras?.allowsPrint ?? true
      : programDecision === "inclusive_access"
      ? inclusiveAccessExtras?.allowsPrint ?? true
      : true;

  return {
    // branded name for the current program, branded names for NP/IA/EA can be found in their respective fields
    name:
      institutionProgram && programDecision
        ? getProgramName(programDecision, config)
        : "",
    // branded description for the current program, branded descriptions for NP/IA/EA can be found in their respective fields
    description:
      institutionProgram && programDecision
        ? getProgramDescription(programDecision, config)
        : "",
    // scope is defined by the user’s institution and determines which programs the user is allowed to choose from. Some scopes
    // allow only a single program type (denoted by a _only suffix), while others allow selection from multiple programs (flexible).
    // If the user is not participating in any programs, the scope is null and users should generally be shown a shopping experience.
    // for most materials. Currently the business refers to this as "Student Choice".
    scope,
    // This field represents the user’s actual program assignment, rather than the allowed scope. It’s used when we need to reflect the
    // program the user is truly in, such as when multiple programs are available (FA) or when the user can opt out (EA). Be aware that
    // when not evaluating scope, logic applies across program scopes.
    userEffectiveProgram: programDecision ?? "no_program",
    // This field indicates whether a user is participating in a program. Some programs (e.g., EA) allow opting out of the program entirely,
    // while others (e.g., IA) require opting out at the item level. For this reason, "participating" was chosen over "opted in/out".
    // This field is especially useful for determining participation in EA and FA scopes.
    participating,
    // the following objects contain branded information for each program type
    allowsPrint,
    np: {
      name: getProgramName("no_program", config),
      description: getProgramDescription("no_program", config),
      allowsPrint: noProgramExtras?.allowsPrint ?? true,
      features: getProgramFeatures("no_program", config) ?? [],
      showPVSActualMarketPrice:
        config.no_program?.showPVSActualMarketPrice ?? false,
    },
    ia: {
      name: getProgramName("inclusive_access", config),
      description: getProgramDescription("inclusive_access", config),
      features: getProgramFeatures("inclusive_access", config) ?? [],
      showPricing: config.inclusive_access.showPricing,
      allowsPrint: inclusiveAccessExtras?.allowsPrint ?? true,
    },
    ea: {
      name: getProgramName("equitable_access", config),
      description: getProgramDescription("equitable_access", config),
      features: getProgramFeatures("equitable_access", config) ?? [],
      optOutMessage: config.equitable_access?.optOutMessage ?? "",
      itemCardDescription: config.equitable_access?.itemCardDescription ?? "",
      updateShippingLink:
        config.equitable_access?.akademos?.updateShippingLink ?? "",
      personalizedValueSheet:
        config.equitable_access?.akademos?.personalizedValueSheet ?? "",
      allowsOptOut: config.equitable_access?.catalogLevelOptOut ?? false,
    },
    // personalized value sheet fields - these are not program-specific, so they
    // are included at the top level rather than within each program type
    personalizedValueSheet: {
      showPriceDrawer: config.personalizedValueSheet?.showPriceDrawer ?? false,
      showPricing: config.personalizedValueSheet?.showPricing ?? false,
      showActualMarketPrice:
        config.personalizedValueSheet?.showActualMarketPrice ?? false,
      showCreditHourBreakdownInPvsDrawer:
        config.personalizedValueSheet?.showCreditHourBreakdownInPvsDrawer ??
        false,
    },
  };
}

// Promise-based variant
export function buildProgramDetailsAsync(args: {
  brand: CoursesContextBrand;
  term: Term;
}): Promise<ProgramDetailsType> {
  return Promise.resolve(buildProgramDetails(args));
}
